import { BrowserModule, HammerModule, HammerGestureConfig, HAMMER_GESTURE_CONFIG, HAMMER_LOADER } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';

import { ClipboardModule } from '@angular/cdk/clipboard';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSliderModule } from '@angular/material/slider';
import { AgentDashboardComponent } from './agent-dashboard/agent-dashboard.component';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { LayoutModule } from '@angular/cdk/layout';
import { MatBadgeModule } from '@angular/material/badge';
// TODO: remove MatChipsModule. The chips we use are now a plain <button class="chip"> (global .chip in styles.css:
// [attr.aria-pressed] for a toggle, .chip-selected / .chip-add otherwise - see /sales and the onboarding
// dialog). Only feed.component.html still uses mat-chip-listbox / mat-chip-option; move those to .chip
// buttons, then drop this import. Do it before (or as part of) the Angular upgrade, so we aren't
// migrating chips we no longer want across Material's breaking changes.
import { MatChipsModule } from '@angular/material/chips';
import { SampleFormComponent } from './sample-form/sample-form.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { ReactiveFormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavComponent } from './nav/nav.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatRippleModule, MatNativeDateModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { CdkStepperModule } from '@angular/cdk/stepper'; // not using this yet, don't even know what it's for [// https://material.angular.io/guide/creating-a-custom-stepper-using-the-cdk-stepper] & MatFormFieldModule
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { StatusComponent } from './status/status.component';
import { CartComponent } from './cart/cart.component';
import { ScrollingModule} from '@angular/cdk/scrolling';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CanExitGuard, AuthenticationService } from './services/authentication.service';
import { FeedComponent } from './feed/feed.component';
import { LocationComponent } from './location/location.component';
import { LoginComponent } from './login/login.component';
import { SignupComponent } from './signup/signup.component';
import { TestComponent } from './test/test.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { HouseDetailDialogComponent } from './dialogs/house-detail-dialog/house-detail-dialog.component';
import { ChatsComponent } from './chats/chats.component';
import { PictureCarouselComponent } from './picture-carousel/picture-carousel.component';
import { ExitConfirmationDialogComponent } from './dialogs/exit-confirmation-dialog/exit-confirmation-dialog.component';
import { WaitlistComponent } from './waitlist/waitlist.component';
import { JoinWaitlistSuccessBottomsheetComponent } from './join-waitlist-success-bottomsheet/join-waitlist-success-bottomsheet.component';
import { ProfileComponent } from './profile/profile.component';
import { CreateNewAccommodationComponent } from './dialogs/create-new-accommodation/create-new-accommodation.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { CreateNewSaleComponent } from './dialogs/create-new-sale/create-new-sale.component';
import { JoinWaitlistSuccessDialogComponent } from './dialogs/join-waitlist-success-dialog/join-waitlist-success-dialog.component';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { EffectsModule } from '@ngrx/effects';
import { CorpMemberEffects } from './ngrx-store/effects/corp-member.effects';
import { SocketEffects } from './ngrx-store/effects/socket.effects';
import { ChatOutboxEffects } from './ngrx-store/effects/chat-outbox.effects';
import { chatOutboxReducer } from './ngrx-store/reducers/chat-outbox.reducer';
import { StoreModule } from '@ngrx/store';
import { corpMemberReducer, feedReducer, messagesReducer, metaReducers, postHistoryReducer, ppasReducer } from './ngrx-store/reducers/corp-member.reducer';
import { AuthInterceptor } from './http-interceptor/auth.interceptor';
import { TextFieldModule } from '@angular/cdk/text-field';
import { corpMemberFeatureKey, feedFeatureKey, messagesFeatureKey, postHistoryFeatureKey, ppasFeatureKey, chatOutboxFeatureKey } from './ngrx-store/selectors/corp.selectors';
import { _FEATURE_EFFECTS } from '@ngrx/effects/src/tokens';
import { PublicProfileComponent } from './public-profile/public-profile.component';
import { AddNewPlaceDialogComponent } from './dialogs/add-new-place-dialog/add-new-place-dialog.component';
import { LeaveReviewComponent } from './dialogs/leave-review/leave-review.component';
import { ErrorResponseInterceptor } from './http-interceptor/error-response.interceptor';
import { CacheInterceptor } from './http-interceptor/cache.interceptor';
import { ProgressButtonComponent } from './custom-components/progress-button/progress-button.component';
import { FeedSaleContentComponent } from './feed-sale-content/feed-sale-content.component';
import { FeedAccommodationContentComponent } from './feed-accommodation-content/feed-accommodation-content.component';
import { FeedContentComponent } from './feed-content/feed-content.component';
import { environment } from 'src/environments/environment';
import { MessagesComponent } from './messages/messages.component';
import { MentionTextareaComponent } from './mention-textarea/mention-textarea.component';
import { SearchPageComponent } from './search-page/search-page.component';
import { SearchResultsComponent } from './search-results/search-results.component';
import { SearchDialogComponent } from './dialogs/search-dialog/search-dialog.component';
import { StarRatingPipe } from './star-rating.pipe';
import { SaleLocationPipe } from './sale-location.pipe';
import { LoginSignupPromptComponent } from './dialogs/login-signup-prompt/login-signup-prompt.component';
import { MoreViewSearchComponent } from './dialogs/more-view-search/more-view-search.component';
import { HomePageComponent } from './home-page/home-page.component';
import { LearnMoreDialogComponent } from './home-page/learn-more-dialog/learn-more-dialog.component';
import { WordTickerComponent } from './word-ticker/word-ticker.component';
import { AboutUsComponent } from './about-us/about-us.component';
import { SiteHeaderComponent } from './site-header/site-header.component';
import { PhotoStripComponent } from './photo-strip/photo-strip.component';
import { ListingPageHeaderComponent } from './listing-page-header/listing-page-header.component';
import { VerifyEmailComponent } from './verify-email/verify-email.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { EmailVerificationBannerComponent } from './email-verification-banner/email-verification-banner.component';
import { OnboardingChecklistComponent } from './onboarding-checklist/onboarding-checklist.component';
import { DecimalPipe } from '@angular/common';
import { ShortenNumberPipe } from './shorten-number.pipe';
import { InitialsPipe } from './initials.pipe';

import * as Sentry from "@sentry/angular";
import { SentryCustomErrorHandler } from './sentry-err-handler';
import { ImgSrcFallbackDirective } from './directives/img-src-fallback.directive';
import { ChatBubbleGestureDirective } from './directives/chat-bubble-gesture.directive';
import { PpaPageComponent } from './ppa-page/ppa-page.component';
import { HousesPageComponent } from './houses-page/houses-page.component';
import { SalesPageComponent } from './sales-page/sales-page.component';
import { DeletePostComponent } from './dialogs/delete-post/delete-post.component';
import { AccommodationChatInfoDialogComponent } from './dialogs/accommodation-chat-info-dialog/accommodation-chat-info-dialog.component';
import { PpaContentCardComponent } from './ppa-content-card/ppa-content-card.component';
import { TeamPageComponent } from './team-page/team-page.component';
import { AdminLoginComponent } from './admin/admin-login/admin-login.component';
import { AdminLayoutComponent } from './admin/admin-layout/admin-layout.component';
import { AdminDashboardComponent } from './admin/admin-dashboard/admin-dashboard.component';
import { AdminUsersComponent } from './admin/admin-users/admin-users.component';
import { AdminPostsComponent } from './admin/admin-posts/admin-posts.component';
import { AdminChatsComponent } from './admin/admin-chats/admin-chats.component';
import { AdminReferralsComponent } from './admin/admin-referrals/admin-referrals.component';
import { AdminPpasComponent } from './admin/admin-ppas/admin-ppas.component';
import { AdminPpaRemoveDialogComponent } from './dialogs/admin-ppa-remove-dialog/admin-ppa-remove-dialog.component';
import { SimilarPpasDialogComponent } from './dialogs/similar-ppas-dialog/similar-ppas-dialog.component';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { ConfirmActionDialogComponent } from './dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { AdminPostDetailsDialogComponent } from './dialogs/admin-post-details-dialog/admin-post-details-dialog.component';
import { PublicSaleComponent } from './public-sale/public-sale.component';
import { PublicAccommodationComponent } from './public-accommodation/public-accommodation.component';
import { PublicPpaComponent } from './public-ppa/public-ppa.component';
import { PpaReviewsMoreComponent } from './ppa-reviews-more/ppa-reviews-more.component';
import { AmbassadorComponent } from './ambassador/ambassador.component';
import { AvatarComponent } from './avatar/avatar.component';
import { HasPublicProfilePipe } from './has-public-profile.pipe';
import { DisplayNamePipe } from './display-name.pipe';
import { MentionOptionDirective } from './mention-textarea/mention-option.directive';
import { ChatTimelinePipe } from './chat-timeline.pipe';
import { ChatListingCardComponent } from './chat-listing-card/chat-listing-card.component';
import { ParseMentionsPipe } from './parse-mentions.pipe';
import { PublicProfileCardComponent } from './public-profile-card/public-profile-card.component';
import { PublicProfileBottomsheetComponent } from './public-profile-bottomsheet/public-profile-bottomsheet.component';
import { BookmarkedMessagesBottomsheetComponent } from './bookmarked-messages-bottomsheet/bookmarked-messages-bottomsheet.component';
import { EnablePushNotificationsDialogComponent } from './dialogs/enable-push-notifications-dialog/enable-push-notifications-dialog.component';
import { OnboardingChecklistDialogComponent } from './dialogs/onboarding-checklist-dialog/onboarding-checklist-dialog.component';
import { ServiceWorkerModule } from '@angular/service-worker';

@NgModule({
  declarations: [
    AppComponent,
    AgentDashboardComponent,
    SampleFormComponent,
    NavComponent,
    StatusComponent,
    CartComponent,
    FeedComponent,
    LocationComponent,
    LoginComponent,
    SignupComponent,
    TestComponent,
    HouseDetailDialogComponent,
    ChatsComponent,
    PictureCarouselComponent,
    ExitConfirmationDialogComponent,
    WaitlistComponent,
    JoinWaitlistSuccessBottomsheetComponent,
    ProfileComponent,
    CreateNewAccommodationComponent,
    CreateNewSaleComponent,
    JoinWaitlistSuccessDialogComponent,
    PublicProfileComponent,
    AddNewPlaceDialogComponent,
    LeaveReviewComponent,
    ProgressButtonComponent,
    FeedSaleContentComponent,
    FeedAccommodationContentComponent,
    FeedContentComponent,
    MessagesComponent,
    SearchPageComponent,
    SearchResultsComponent,
    SearchDialogComponent,
    StarRatingPipe,
    SaleLocationPipe,
    LoginSignupPromptComponent,
    MoreViewSearchComponent,
    HomePageComponent,
    LearnMoreDialogComponent,
    WordTickerComponent,
    AboutUsComponent,
    SiteHeaderComponent,
    PhotoStripComponent,
    ListingPageHeaderComponent,
    VerifyEmailComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    EmailVerificationBannerComponent,
    OnboardingChecklistComponent,
    ShortenNumberPipe,
    InitialsPipe,
    ImgSrcFallbackDirective,
    ChatBubbleGestureDirective,
    PpaPageComponent,
    HousesPageComponent,
    SalesPageComponent,
    DeletePostComponent,
    AccommodationChatInfoDialogComponent,
    PpaContentCardComponent,
    TeamPageComponent,
    AdminLoginComponent,
    AdminLayoutComponent,
    AdminDashboardComponent,
    AdminUsersComponent,
    AdminPostsComponent,
    AdminChatsComponent,
    AdminReferralsComponent,
    AdminPpasComponent,
    AdminPpaRemoveDialogComponent,
    SimilarPpasDialogComponent,
    ConfirmActionDialogComponent,
    AdminPostDetailsDialogComponent,
    PublicSaleComponent,
    PublicAccommodationComponent,
    PublicPpaComponent,
    PpaReviewsMoreComponent,
    AmbassadorComponent,
    AvatarComponent,
    HasPublicProfilePipe,
    DisplayNamePipe,
    MentionOptionDirective,
    ChatTimelinePipe,
    ChatListingCardComponent,
    ParseMentionsPipe,
    PublicProfileCardComponent,
    PublicProfileBottomsheetComponent,
    BookmarkedMessagesBottomsheetComponent,
    EnablePushNotificationsDialogComponent,
    OnboardingChecklistDialogComponent,
    MentionTextareaComponent,
  ],
  imports: [
    NgOptimizedImage,
    MatNativeDateModule,
    MatExpansionModule,
    MatDatepickerModule,
    MatTabsModule,
    MatCheckboxModule,
    BrowserModule,
    CdkStepperModule,
    MatStepperModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    ScrollingModule,
    AppRoutingModule,
    HttpClientModule,
    MatButtonToggleModule,
    BrowserAnimationsModule,
    MatSliderModule,
    MatGridListModule,
    MatAutocompleteModule,
    MatCardModule,
    MatBottomSheetModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    LayoutModule,
    MatChipsModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    ReactiveFormsModule,
    MatToolbarModule,
    MatSnackBarModule,
    MatSidenavModule,
    MatListModule,
    FormsModule,
    MatRippleModule,
    MatTooltipModule,
    MatDialogModule,
    MatProgressBarModule,
    ClipboardModule,
    HammerModule,
    MatBadgeModule,
    MatFormFieldModule,
    MatTableModule,
    MatPaginatorModule,
    TextFieldModule,
    EffectsModule.forRoot([CorpMemberEffects, SocketEffects, ChatOutboxEffects]),
    StoreModule.forRoot({
      [corpMemberFeatureKey]: corpMemberReducer,
      [feedFeatureKey]: feedReducer,
      [messagesFeatureKey]: messagesReducer,
      [postHistoryFeatureKey]: postHistoryReducer,
      [ppasFeatureKey]: ppasReducer,
      [chatOutboxFeatureKey]: chatOutboxReducer,
    }, {metaReducers: metaReducers}),
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.production, registrationStrategy: 'registerWhenStable:30000' }),
  ],
  providers: [
    DecimalPipe,
    CanExitGuard,
    AuthenticationService,
    // {
    //   provide: _FEATURE_EFFECTS,
    //   useValue: 
    // },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerGestureConfig
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }, // https://stackoverflow.com/a/62839144/9259701
    {
      provide: HTTP_INTERCEPTORS, useClass: ErrorResponseInterceptor, multi: true
    },
    {
      provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true
    },
    {
      // Registered last so it sits closest to the actual HTTP call - a cache
      // hit short-circuits before the request ever reaches the backend.
      provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true
    },

    // Sentry
    {
      provide: ErrorHandler,
      useClass: SentryCustomErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
